import React from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import {
    Home,
    MessageCircle,
    Users,
    Car,
    Bookmark,
    Plus,
    User,
    LogOut,
    Shield,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { absUrl } from "@/lib/api";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/**
 * Persistent top nav — iPad & Desktop only. Angular, no pills.
 *   - Mobile (< md): hidden. BottomTabBar owns the phone.
 *   - iPad (md → lg): icons + labels, compact spacing, thinner bar.
 *   - Desktop (>= lg): full labels, roomier spacing.
 * One account model: everyone can browse and post.
 */

const TABS = [
    { to: "/marketplace", label: "Market", icon: Home, testid: "nav-marketplace" },
    { to: "/community", label: "Feed", icon: Users, testid: "nav-community" },
    { to: "/favorites", label: "Saved", icon: Bookmark, testid: "nav-saved" },
    { to: "/messages", label: "Inbox", icon: MessageCircle, testid: "nav-messages" },
    { to: "/garage", label: "Garage", icon: Car, testid: "nav-garage" },
];

export default function TopNavBar({ onFabClick }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    return (
        <header
            data-testid="top-nav-bar"
            className="hidden md:block sticky top-0 z-40"
            style={{
                background: "rgba(6,6,6,0.9)",
                borderBottom: "1px solid #262626",
                backdropFilter: "blur(18px)",
                WebkitBackdropFilter: "blur(18px)",
            }}
        >
            <div className="max-w-7xl mx-auto px-4 lg:px-6 h-14 lg:h-16 flex items-center gap-3 lg:gap-5">
                {/* Brand */}
                <Link to="/marketplace" className="shrink-0" data-testid="top-nav-logo">
                    <Logo size="sm" />
                </Link>

                {/* Primary nav — sharp underlined tabs */}
                <nav className="flex items-stretch gap-0 ml-3 h-full">
                    {TABS.map((t) => (
                        <NavLink
                            key={t.to}
                            to={t.to}
                            data-testid={t.testid}
                            end={t.to === "/marketplace"}
                            className={({ isActive }) =>
                                `relative flex items-center gap-1.5 px-3 lg:px-4 text-xs lg:text-sm font-semibold ${
                                    isActive
                                        ? "text-white"
                                        : "text-onyx-500 hover:text-white"
                                }`
                            }
                            style={{
                                letterSpacing: "0.04em",
                                transition: "color 0.15s",
                            }}
                        >
                            {({ isActive }) => (
                                <>
                                    <t.icon className="w-4 h-4" strokeWidth={2.2} />
                                    <span>{t.label}</span>
                                    {isActive && (
                                        <span
                                            className="absolute bottom-0 left-2 right-2 h-[2px]"
                                            style={{
                                                background: "#E10600",
                                                boxShadow: "0 0 10px rgba(225,6,0,0.55)",
                                            }}
                                        />
                                    )}
                                </>
                            )}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex-1" />

                {/* Post/Create — angular button */}
                <button
                    data-testid="top-nav-create"
                    onClick={() => {
                        if (!user) return navigate("/login");
                        onFabClick?.();
                    }}
                    className="hidden lg:inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-white"
                    style={{
                        borderRadius: 6,
                        background:
                            "linear-gradient(180deg, #ff3b33 0%, #E10600 55%, #8a0400 100%)",
                        boxShadow:
                            "0 6px 18px rgba(225,6,0,0.35), inset 0 1px 0 rgba(255,255,255,0.14)",
                        letterSpacing: "0.08em",
                    }}
                >
                    <Plus className="w-4 h-4" strokeWidth={2.6} />
                    <span>POST</span>
                </button>

                {/* User menu / sign in */}
                {user ? (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                data-testid="top-nav-user"
                                className="w-9 h-9 lg:w-10 lg:h-10 overflow-hidden flex items-center justify-center"
                                style={{
                                    borderRadius: 6,
                                    background: "#141414",
                                    border: "1px solid #262626",
                                }}
                            >
                                {user.avatar_url ? (
                                    <img
                                        src={absUrl(user.avatar_url)}
                                        alt="me"
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <User className="w-4 h-4 text-onyx-500" />
                                )}
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                            align="end"
                            className="surface border-onyx-600 min-w-[200px]"
                        >
                            <DropdownMenuLabel className="text-white">
                                {user.name}
                            </DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => navigate("/garage")}>
                                <Car className="w-4 h-4 mr-2" /> Garage
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate("/favorites")}>
                                <Bookmark className="w-4 h-4 mr-2" /> Saved
                            </DropdownMenuItem>
                            {user.role === "admin" && (
                                <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                        onClick={() => navigate("/admin/control")}
                                        data-testid="top-nav-admin-control"
                                    >
                                        <Shield className="w-4 h-4 mr-2" /> Control Panel
                                    </DropdownMenuItem>
                                </>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                onClick={async () => {
                                    await logout();
                                    navigate("/");
                                }}
                                data-testid="top-nav-logout"
                            >
                                <LogOut className="w-4 h-4 mr-2" /> Log out
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                ) : (
                    <Link
                        to="/login"
                        className="btn-red px-4 py-2 text-xs font-bold"
                        data-testid="top-nav-signin"
                        style={{ borderRadius: 6 }}
                    >
                        Sign in
                    </Link>
                )}
            </div>
        </header>
    );
}
